import { ManualTaskStatus, ManualTaskType } from '@compass/client/types/manual_task';

import { humanize } from '@/utils/fields';

type BadgeVariant = 'outline' | 'progress' | 'success' | 'secondary';

/** Statuses a task never leaves. Excluded from the My Tasks Pending tab. */
const TERMINAL_TASK_STATUSES = new Set<ManualTaskStatus>([
  ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED,
  ManualTaskStatus.MANUAL_TASK_STATUS_CANCELLED,
]);

/** Statuses offered in the My Tasks status filter, in lifecycle order. */
export const TASK_FILTER_STATUSES: ManualTaskStatus[] = [
  ManualTaskStatus.MANUAL_TASK_STATUS_PENDING,
  ManualTaskStatus.MANUAL_TASK_STATUS_STARTED,
  ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED,
  ManualTaskStatus.MANUAL_TASK_STATUS_CANCELLED,
];

// Pending is the normal starting state, so it is neutral rather than a warning colour.
const STATUS_BADGE_VARIANTS: Partial<Record<ManualTaskStatus, BadgeVariant>> = {
  [ManualTaskStatus.MANUAL_TASK_STATUS_PENDING]: 'secondary',
  [ManualTaskStatus.MANUAL_TASK_STATUS_STARTED]: 'progress',
  [ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED]: 'success',
  [ManualTaskStatus.MANUAL_TASK_STATUS_CANCELLED]: 'outline',
};

/** True for a task that still needs action: any known status that is not terminal. */
export const isPendingTask = (status: ManualTaskStatus | undefined): boolean =>
  status !== undefined &&
  status !== ManualTaskStatus.MANUAL_TASK_STATUS_UNSPECIFIED &&
  !TERMINAL_TASK_STATUSES.has(status);

// Labels come from the enum names ("MANUAL_TASK_TYPE_FILL_MISSING_INFO" -> "Fill Missing Info"),
// so a type added to the proto gets a readable label with no change here.
const enumLabel = (name: string | undefined, prefix: string): string =>
  name && name !== `${prefix}UNSPECIFIED` ? humanize(name.slice(prefix.length)) : 'Unknown';

export const taskTypeLabel = (type: ManualTaskType | undefined): string =>
  enumLabel(type === undefined ? undefined : ManualTaskType[type], 'MANUAL_TASK_TYPE_');

export const taskStatusLabel = (status: ManualTaskStatus | undefined): string =>
  enumLabel(status === undefined ? undefined : ManualTaskStatus[status], 'MANUAL_TASK_STATUS_');

export const taskStatusBadgeVariant = (status: ManualTaskStatus | undefined): BadgeVariant =>
  (status !== undefined && STATUS_BADGE_VARIANTS[status]) || 'secondary';
